import { ofType } from 'redux-observable';
import React from 'react';
import { merge, Observable, of, EMPTY } from 'rxjs';
import { mergeMap, switchMap, catchError } from 'rxjs/operators';
import { ModalService } from 'ui-components/services/modal-service';
import { NOTIFICATION_TYPE_ERROR, NOTIFICATION_TYPE_SUCCESS } from 'ui-components/services/notification-service';
import { NotificationHubService } from '../../notification-hub/notification-hub-service';
import { NotificationType } from '../../notification-hub/notification-hub.types';
import { FormattedMessage } from 'react-intl';

import * as Actions from './customers.import.actions';
import * as CustomerActions from '../customers.actions';
import * as Api from './customers.import.api';
import ImportCustomersModal from '../../../pages/customers-page/modals/import-customers-modal';
import ImportResultsModal from '../../../pages/customers-page/modals/import-customer-results-modal';
import { pusherTimeout$ } from '../../pusher/pusher.timeout.observable';
import { handleCustomerImport } from '../../pusher/pusher.customer-import.observables';
import history from '../../../history';
import { messages } from './messages';
import { noop } from 'ui-components/default-props/noop';

let modalId;

const showFailureNotification = () => {
  NotificationHubService.showNotification(
    {
      notificationType: NotificationType.CUSTOMERS_IMPORT_FAILED,
      defaultTitle: messages.customerImportLogFailureNotificationTitle.defaultMessage,
      defaultDescription: messages.customerImportFailureNotificationBody.defaultMessage,
    },
    {
      title: <FormattedMessage {...messages.customerImportLogFailureNotificationTitle} />,
      content: <FormattedMessage {...messages.customerImportFailureNotificationBody} />,
      notificationType: NOTIFICATION_TYPE_ERROR,
      progress: false,
      dontHide: true,
      duration: 3000,
    },
  );
};

export const prepareToOpenImportCustomersModal = (action$) => {
  return action$.pipe(
    ofType(Actions.PREPARE_IMPORT_CUSTOMERS_MODAL_OPEN),
    mergeMap(() => {
      modalId = ModalService.launchModal({
        component: ImportCustomersModal,
        height: '100%',
        maxHeight: '100%',
      });
      return EMPTY;
    }),
  );
};

export const startImport = (action$) => {
  return action$.pipe(
    ofType(Actions.INITIATE_CUSTOMERS_CSV_IMPORT),
    switchMap((action) => {
      return Api.postCustomerImportCsv(action.ignoreCsvRecord, action.file).pipe(
        mergeMap((response) => {
          const batchId = response.data.batchId;
          const handleMessage$ = handleCustomerImport(batchId);
          const timeout$ = pusherTimeout$({
            action$,
            actionTypes: [],
            clearTimeout$: handleMessage$,
            notificationFunc: noop,
          });
          return merge(
            handleMessage$.pipe(
              mergeMap(() => {
                return of(Actions.importSuccess(batchId));
              }),
            ),
            timeout$,
          );
        }),
        catchError(() => {
          showFailureNotification();
          return EMPTY;
        }),
      );
    }),
  );
};

const initiateCustomerImportCompleteNotification$ = (failedCount = 0) => {
  return new Observable((observer) => {
    NotificationHubService.showNotification(
      {
        notificationType: NotificationType.CUSTOMERS_IMPORTED,
        defaultTitle: messages.customerImportCompleteNotificationTitle.defaultMessage,
        defaultDescription: `Your customer import finished successfully.${failedCount > 0 ? ` ${failedCount} customers failed to import.` : ''}`,
        metadataJson: JSON.stringify({ failedCount }),
      },
      {
        title: <FormattedMessage {...messages.customerImportCompleteNotificationTitle} />,
        content: <FormattedMessage {...messages.customerImportCompleteNotificationBody} />,
        notificationType: NOTIFICATION_TYPE_SUCCESS,
        progress: false,
        dontHide: true,
        buttons: [
          {
            text: <FormattedMessage {...messages.customerImportCompleteStoreRefresh} />,
            theme: 'link',
            callback: () => {
              history.push('/customers');
              observer.next(CustomerActions.requestGetCustomers());
            },
          },
        ],
        onClose: () => observer.complete(),
      },
    );
  }).pipe(mergeMap((next) => of(next)));
};

const showErrorLogModal = (logs) => {
  ModalService.launchModal({
    component: ImportResultsModal,
    props: {
      logs,
    },
  });
};

export const afterImportSuccess = (action$) => {
  return action$.pipe(
    ofType(Actions.CUSTOMERS_CSV_IMPORT_SUCCESS),
    switchMap((action) => {
      return Api.postImportLog(action.batchId).pipe(
        mergeMap((response) => {
          const logs = response.data.logs;
          const failedCount = logs ? logs.length : 0;
          if (failedCount > 0) {
            showErrorLogModal(logs);
          }
          ModalService.closeModalById(modalId);
          return initiateCustomerImportCompleteNotification$(failedCount);
        }),
        catchError((error) => {
          return of(Actions.customerImportFailure(error));
        }),
      );
    }),
  );
};

export const afterImportFailure = (action$) => {
  return action$.pipe(
    ofType(Actions.CUSTOMERS_IMPORT_FAILURE),
    mergeMap(() => {
      showFailureNotification();
      ModalService.closeModalById(modalId);

      return EMPTY;
    }),
  );
};
